// <book-studio>  the open book beside a name box; the book redraws as the visitor types.
// <kb-cover>     a book's front cover, drawn with the name the visitor has typed.
// <book-reader>  a finished book, opened from the link on an order.

(() => {
  const Art = window.KBArt;
  const Books = window.KBBooks;
  const SAMPLE = 'Anaya';
  const STORE = 'kb-book';
  const SUGGESTED = ['Mummy', 'Papa', 'Dadi', 'Dadu', 'Nani', 'Nana', 'Didi', 'Bhaiya', 'Chacha', 'Chachi', 'Mama', 'Mami', 'Bua', 'Masi'];
  const FACES = ['none', 'moustache', 'beard'];
  const MAX_FAMILY = 10;

  // A look travels in the order link as eight digits: who, skin, hair, hair colour, glasses, face, bindi, outfit.
  const lookCode = (look) => [look.who === 'child' ? 1 : 0, look.skin, Art.HAIR_STYLES.indexOf(look.hair), look.hairColour, look.glasses ? 1 : 0, FACES.indexOf(look.face), look.bindi ? 1 : 0, look.outfit].join('');
  const lookFromCode = (code, fallback) => {
    if (!/^\d{8}$/.test(code || '')) return fallback;
    const d = code.split('').map(Number);
    return { who: d[0] ? 'child' : 'grown-up', skin: Math.min(d[1], 4), hair: Art.HAIR_STYLES[d[2]] || 'short', hairColour: Math.min(d[3], 3), glasses: !!d[4], face: FACES[d[5]] || 'none', bindi: !!d[6], outfit: d[7] };
  };
  // The book's details travel in the order link as url-safe base64, so names are not readable in the address.
  const pack = (data) => btoa(unescape(encodeURIComponent(JSON.stringify(data)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const unpack = (text) => JSON.parse(decodeURIComponent(escape(atob(text.replace(/-/g, '+').replace(/_/g, '/')))));
  // Tells the store's analytics (Settings > Customer events) what visitors do with the books.
  // Names and messages are never sent.
  const track = (event, data = {}) => {
    try {
      window.Shopify.analytics.publish(event, data);
    } catch (error) {
      // No analytics on this page.
    }
  };
  const tidyName = (name) => name.trim().replace(/\s+/g, ' ').slice(0, 20);
  const member = (call, index) => ({ call, look: Art.lookFor(call, index) });
  const freshState = () => ({ name: '', message: '', child: { ...Art.lookFor('', 0, 'child'), hair: 'short' }, family: ['Mummy', 'Papa', 'Dadi', 'Dadu'].map(member) });

  function loadState() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORE));
      if (saved && saved.child && Array.isArray(saved.family)) return { ...freshState(), ...saved };
    } catch (error) {
      // No saved book, or storage is switched off.
    }
    return freshState();
  }
  function saveState(state) {
    try {
      localStorage.setItem(STORE, JSON.stringify(state));
    } catch (error) {
      // The book still works without being remembered.
    }
    window.dispatchEvent(new CustomEvent('kb:change'));
  }

  // What the page templates need: the sample name stands in until one is typed.
  const context = (state, key) => ({ name: tidyName(state.name) || SAMPLE, child: state.child, family: Books.BOOKS[key].family ? state.family.filter((person) => person.call.trim()) : [], message: state.message.trim() });

  // Reads a finished book back out of its link: which book, and who is in it.
  function fromLink(params) {
    let data;
    try {
      data = unpack(params.get('d'));
    } catch (error) {
      data = null;
    }
    const key = params.get('book');
    if (!data || !data.n || !Books.BOOKS[key]) return null;
    return {
      key,
      x: {
        name: tidyName(String(data.n)),
        message: String(data.m || '').slice(0, 160),
        child: lookFromCode(data.c, Art.lookFor('', 0, 'child')),
        family: (Array.isArray(data.f) ? data.f : []).slice(0, MAX_FAMILY).map(([call, code], index) => ({ call: String(call).slice(0, 16), look: lookFromCode(code, Art.lookFor(call, index)) })),
      },
    };
  }

  function ensureDefs() {
    if (!document.getElementById('kb-paint')) document.body.insertAdjacentHTML('beforeend', Art.defs());
  }
  const face = (look) => Art.page(`<g transform="translate(-60 -44) scale(1.3)">${Art.place(Art.person(look), 200, 400, 0.96)}</g>`);

  // ----- pictures as flat images -----
  // Printed straight from the page, the paint filters turn every picture into a huge lossless image
  // (13 MB and more for one book). So for printing each picture is first flattened to a JPEG;
  // the words stay as sharp text on top.

  const FLAT = 1600; // pixels across a flattened picture: about 190 to the inch on a 21 cm page
  const FLAT_PRINT = 2552; // for the printer: 300 to the inch on a 21.6 cm sheet

  const inRange = (range, codes) =>
    !range ||
    range.split(',').some((part) => {
      const found = part.trim().match(/^U\+([0-9A-F?]+)(?:-([0-9A-F]+))?$/i);
      if (!found) return false;
      const low = parseInt(found[1].replace(/\?/g, '0'), 16);
      const high = parseInt(found[2] || found[1].replace(/\?/g, 'F'), 16);
      return codes.some((code) => code >= low && code <= high);
    });

  // The picture font, as CSS a stand-alone picture can carry: only the parts that hold the letters used.
  async function pictureFont(letters) {
    const codes = [...new Set(letters)].map((ch) => ch.codePointAt(0));
    if (!codes.length) return '';
    const css = await (await fetch(document.querySelector('link[rel="stylesheet"][href*="fonts.googleapis.com"]').href)).text();
    const faces = (css.match(/@font-face\s*{[^}]*}/g) || []).filter((face) => /Baloo 2/.test(face) && /font-weight:\s*800/.test(face) && inRange((face.match(/unicode-range:\s*([^;]+)/) || [])[1], codes));
    const carried = faces.map(async (face) => {
      const url = face.match(/url\(([^)]+)\)/)[1];
      const file = await (await fetch(url)).blob();
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      return face.replace(url, data);
    });
    return (await Promise.all(carried)).join('');
  }

  // One page's picture on its background colour, as a JPEG that is size pixels across.
  async function flatten(art, bg, font, size) {
    const extras = `${Art.defs().match(/<defs>[\s\S]*<\/defs>/)[0]}<style>${font}</style>`;
    const source = art.replace('<svg ', `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" `).replace('>', `>${extras}`);
    const url = URL.createObjectURL(new Blob([source], { type: 'image/svg+xml;charset=utf-8' }));
    try {
      const picture = new Image();
      picture.src = url;
      await picture.decode();
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const pen = canvas.getContext('2d');
      pen.fillStyle = bg;
      pen.fillRect(0, 0, size, size);
      pen.drawImage(picture, 0, 0, size, size);
      return await new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No picture'))), 'image/jpeg', 0.86));
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  const lettersIn = (pages) => pages.map((page) => (page.art.match(/>[^<]+<\/text>/g) || []).join('')).join('').replace(/<\/text>|>/g, '');

  class KbCover extends HTMLElement {
    connectedCallback() {
      ensureDefs();
      this.draw = this.draw.bind(this);
      window.addEventListener('kb:change', this.draw);
      this.draw();
    }

    disconnectedCallback() {
      window.removeEventListener('kb:change', this.draw);
    }

    draw() {
      // A cover in the cart shows the book that was made, read from its link; elsewhere, the name being typed.
      const made = this.dataset.link ? fromLink(new URL(this.dataset.link, window.location.href).searchParams) : null;
      if (this.dataset.link && !made) return;
      const key = made ? made.key : this.dataset.book || Books.keyFor(this.dataset.handle);
      this.innerHTML = Books.pageHtml(Books.pages(key, made ? made.x : context(loadState(), key))[0], 1);
    }
  }

  class BookStudio extends HTMLElement {
    connectedCallback() {
      ensureDefs();
      this.products = JSON.parse(this.querySelector('[data-products]').textContent);
      this.keys = Object.keys(Books.BOOKS).filter((key) => this.products[Books.BOOKS[key].handle]);
      if (!this.keys.length) return;
      this.key = this.keys.includes(this.dataset.book) ? this.dataset.book : this.keys[0];
      this.state = loadState();
      this.editing = null; // 'child', or the index of a family member
      this.removed = null; // the last person taken out of the book, so they can be put back
      this.tidyFamily();
      // Pictures are drawn only for pages near the one being read, which keeps typing quick on slow phones.
      this.pages = [];
      this.drawn = 0;
      this.watcher = new IntersectionObserver((entries) => entries.forEach((entry) => entry.isIntersecting && this.paint(entry.target)), { root: this.querySelector('[data-pages]'), rootMargin: '0px 150%' });

      const $ = (selector) => this.querySelector(selector);
      this.nameInput = $('[data-name]');
      this.messageInput = $('[data-message]');
      this.book = $('[data-pages]');
      this.count = $('[data-count]');
      this.options = $('[data-options]');
      this.buyButton = $('[data-buy]');
      this.peek = $('[data-peek]');
      this.bar = $('[data-bar]');
      this.nameInput.value = this.state.name;
      this.messageInput.value = this.state.message;

      this.nameInput.addEventListener('input', () => {
        if (!this.named && this.nameInput.value.trim()) {
          this.named = true;
          track('kb_name_typed', { book: this.key });
        }
        if (this.nameInput.value.trim()) this.say('[data-name-error]', '');
        this.state.name = this.nameInput.value;
        this.showName();
        this.change({}, true);
      });
      this.messageInput.addEventListener('input', () => {
        this.wanted = 'child'; // the message is on the page that shows the child
        this.change({ message: this.messageInput.value }, true);
      });
      $('[data-prev]').addEventListener('click', () => this.turn(-1));
      $('[data-next]').addEventListener('click', () => this.turn(1));
      this.buyButton.addEventListener('click', () => this.buy());
      $('[data-bar-buy]').addEventListener('click', () => this.buy());
      $('[data-peek-back]').addEventListener('click', () => this.book.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      this.book.addEventListener('scroll', () => this.showCount(), { passive: true });
      this.book.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.turn(1);
        if (event.key === 'ArrowLeft') this.turn(-1);
      });
      $('[data-tabs]').addEventListener('click', (event) => {
        const tab = event.target.closest('[data-key]');
        if (tab) {
          this.choose(tab.dataset.key);
          track('kb_book_chosen', { book: tab.dataset.key });
        }
      });
      $('[data-family]').addEventListener('click', (event) => this.familyClick(event));
      $('[data-family]').addEventListener('input', (event) => {
        if (event.target.matches('[data-call]')) {
          const one = this.state.family[this.editing];
          one.call = event.target.value.slice(0, 16);
          this.wanted = one;
          this.change({}, true);
          this.querySelector(`[data-person="${this.editing}"] span:last-child`).textContent = event.target.value || 'Name';
        }
      });

      // The small book and the bar at the bottom follow the page as it scrolls, and step aside for the keyboard.
      this.place = this.place.bind(this);
      this.later = () => {
        if (!this.queued) this.queued = requestAnimationFrame(this.place);
      };
      window.addEventListener('scroll', this.later, { passive: true });
      window.addEventListener('resize', this.later);
      this.addEventListener('focusin', (event) => {
        this.typing = event.target.matches('input[type="text"]');
        this.later();
      });
      this.addEventListener('focusout', () => {
        this.typing = false;
        this.later();
      });
      this.peek.hidden = false;
      this.bar.hidden = false;

      this.drawTabs();
      this.choose(this.key);
      this.showName();
      this.place();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.later);
      window.removeEventListener('resize', this.later);
    }

    change(patch, slow) {
      Object.assign(this.state, patch);
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        saveState(this.state);
        this.drawBook();
      }, slow ? 180 : 0);
    }

    // Shows a message, or hides it when there is nothing to say.
    say(selector, words) {
      const place = this.querySelector(selector);
      place.textContent = words;
      place.hidden = !words;
      if (selector === '[data-name-error]') this.nameInput.setAttribute('aria-invalid', words ? 'true' : 'false');
    }

    // The name appears under the child's face and beside the button, for one last look at the spelling.
    showName() {
      const name = tidyName(this.state.name);
      const label = this.querySelector('[data-person="child"] span:last-child');
      if (label) label.textContent = name || 'Your child';
      const check = this.querySelector('[data-for]');
      check.hidden = !name;
      check.innerHTML = name ? `This book says <strong>${Art.esc(name)}</strong>. Check the spelling before you buy.` : '';
      this.nameInput.classList.toggle('kb-input--long', this.state.name.trim().length > 12);
    }

    // ----- which book -----

    drawTabs() {
      const tabs = this.querySelector('[data-tabs]');
      tabs.hidden = this.keys.length < 2;
      tabs.innerHTML = this.keys
        .map((key) => {
          const product = this.products[Books.BOOKS[key].handle];
          return `<button type="button" class="kb-tab" data-key="${key}" aria-pressed="false" aria-label="${Art.esc(product.title)}"><kb-cover class="kb-tab__cover" data-book="${key}" aria-hidden="true"></kb-cover><span><span class="kb-tab__name">${Art.esc(product.title)}</span><span class="kb-tab__blurb">${Art.esc(product.blurb)}</span></span></button>`;
        })
        .join('');
    }

    choose(key) {
      this.key = key;
      const product = this.products[Books.BOOKS[key].handle];
      this.querySelectorAll('[data-key]').forEach((tab) => tab.setAttribute('aria-pressed', tab.dataset.key === key));
      this.querySelector('[data-price]').textContent = product.price;
      this.querySelector('[data-bar-price]').textContent = product.price;
      this.querySelector('[data-members]').hidden = !Books.BOOKS[key].family;
      this.querySelector('[data-check]').hidden = true;
      if (!Books.BOOKS[key].family && this.editing !== 'child') this.editing = null;
      this.drawFamily();
      this.drawBook(true);
    }

    // ----- the book -----

    drawBook(fromStart) {
      const pages = Books.pages(this.key, context(this.state, this.key));
      const at = fromStart ? 0 : this.book.scrollLeft / (this.book.scrollWidth || 1);
      let html = '';
      for (let i = 0; i < pages.length; i += 2) html += `<div class="kb-spread">${Books.pageHtml(pages[i], i + 1, false)}${Books.pageHtml(pages[i + 1], i + 2, false)}</div>`;
      this.watcher.disconnect();
      this.book.innerHTML = html;
      this.pages = pages;
      this.drawn += 1;
      this.book.querySelectorAll('.kb-page').forEach((page, index) => {
        page.dataset.index = index;
        this.watcher.observe(page);
      });
      this.total = pages.length;
      this.book.scrollLeft = at * this.book.scrollWidth;
      // After a change to one person, the book opens at the page that shows them.
      const index = this.wanted ? pages.findIndex((page) => page.who === this.wanted) : -1;
      if (index >= 0) this.book.scrollLeft = Math.floor(index / this.perView()) * this.book.clientWidth;
      this.wanted = null;
      this.showCount();
    }

    paint(page) {
      this.watcher.unobserve(page);
      page.querySelector('.kb-page__art').innerHTML = this.pages[page.dataset.index].art;
    }

    perView() {
      return window.matchMedia('(max-width: 749px)').matches ? 1 : 2;
    }

    turn(direction) {
      this.book.scrollBy({ left: direction * this.book.clientWidth, behavior: 'smooth' });
    }

    showCount() {
      const per = this.perView();
      const view = Math.round(this.book.scrollLeft / (this.book.clientWidth || 1));
      const first = view * per + 1;
      this.count.textContent = per === 1 ? `Page ${first} of ${this.total}` : `Pages ${first}–${first + 1} of ${this.total}`;
      this.querySelector('[data-prev]').disabled = view === 0;
      this.querySelector('[data-next]').disabled = first + per > this.total;
      if (view > 0 && first + per > this.total && this.finished !== this.key) {
        this.finished = this.key;
        track('kb_book_read_to_end', { book: this.key, pages: this.total });
      }
      this.drawPeek();
    }

    // ----- on a phone: the small book at the top, the price and button at the bottom -----

    // The book is one column on a phone or tablet. Once it has scrolled away, a small copy of the open
    // pages follows the visitor through the choices, so every change can still be seen.
    place() {
      this.queued = 0;
      const narrow = window.matchMedia('(max-width: 989px)').matches;
      const header = document.querySelector('.kb-header');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const book = this.book.getBoundingClientRect();
      const options = this.options.getBoundingClientRect();
      const studio = this.getBoundingClientRect();
      const tall = window.innerHeight;
      this.style.setProperty('--kb-top', `${top}px`);
      // Less than half of the book showing counts as scrolled away.
      this.peeking = narrow && book.bottom - top < book.height * 0.5 && options.bottom > top + 260;
      this.peek.toggleAttribute('data-on', this.peeking);
      this.bar.toggleAttribute('data-on', narrow && !this.typing && studio.top < tall * 0.7 && this.buyButton.getBoundingClientRect().top > tall - 30);
      this.drawPeek();
    }

    drawPeek() {
      if (!this.peeking) return;
      const first = Math.floor((Math.round(this.book.scrollLeft / (this.book.clientWidth || 1)) * this.perView()) / 2) * 2;
      const now = `${this.drawn}:${first}`;
      if (this.peeked === now) return;
      this.peeked = now;
      this.querySelector('[data-peek-pages]').innerHTML = [first, first + 1].filter((i) => i < this.total).map((i) => Books.pageHtml(this.pages[i], i + 1)).join('');
    }

    // ----- the people -----

    // The person being changed: 'child', a family member, or nobody.
    current() {
      return this.editing === 'child' ? 'child' : this.state.family[this.editing] || null;
    }

    // Someone added but never given a name is not in the book, so they leave the list of faces too.
    // A family book always keeps somebody, so the faces and the book never disagree.
    tidyFamily() {
      const open = this.current();
      this.state.family = this.state.family.filter((one) => one.call.trim());
      if (!this.state.family.length) {
        this.state.family = freshState().family;
        this.state.familyChecked = false;
      }
      if (open && open !== 'child') this.editing = this.state.family.includes(open) ? this.state.family.indexOf(open) : null;
    }

    drawFamily() {
      const showFamily = Books.BOOKS[this.key].family;
      const person = (id, label, look) => `<li><button type="button" class="kb-person" data-person="${id}" data-id="person-${id}" aria-expanded="${this.editing === id}"><span class="kb-person__face">${face(look)}</span><span>${Art.esc(label)}</span></button></li>`;
      let people = person('child', tidyName(this.state.name) || 'Your child', this.state.child);
      if (showFamily) people += this.state.family.map((one, index) => person(index, one.call || 'Name', one.look)).join('');
      this.querySelector('[data-people]').innerHTML = people;

      const used = this.state.family.map((one) => one.call.toLowerCase());
      const chips = this.querySelector('[data-chips]');
      chips.hidden = !showFamily || this.state.family.length >= MAX_FAMILY;
      chips.innerHTML = [...SUGGESTED.filter((call) => !used.includes(call.toLowerCase())).slice(0, 6), 'Someone else'].map((call) => `<button type="button" class="kb-chip" data-add="${call}" data-id="add-${call}">+ ${call}</button>`).join('');

      const undo = this.querySelector('[data-undo]');
      undo.hidden = !showFamily || !this.removed || this.state.family.length >= MAX_FAMILY;
      undo.innerHTML = this.removed ? `${Art.esc(this.removed.one.call)} is out of the book. <button type="button" class="kb-link" data-undo-go data-id="undo">Put ${Art.esc(this.removed.one.call)} back</button>` : '';
      this.drawEditor();
    }

    drawEditor() {
      const box = this.querySelector('[data-editor]');
      const isChild = this.editing === 'child';
      const one = isChild ? { look: this.state.child } : this.state.family[this.editing];
      box.hidden = !one;
      if (!one) return;
      const look = one.look;
      const pressed = (on) => `aria-pressed="${on}"`;
      const row = (label, buttons) => `<div class="kb-editor__row" role="group" aria-label="${label}"><span class="kb-editor__label">${label}</span>${buttons}</div>`;
      const skin = Art.SKINS.map((colour, i) => `<button type="button" class="kb-swatch" style="--kb-swatch:${colour}" data-set="skin" data-value="${i}" data-id="skin-${i}" ${pressed(look.skin === i)} aria-label="Skin tone ${i + 1}"></button>`).join('');
      const hair = Art.HAIR_STYLES.map((style) => `<button type="button" class="kb-swatch kb-swatch--face" data-set="hair" data-value="${style}" data-id="hair-${style}" ${pressed(look.hair === style)} aria-label="${style} hair">${face({ ...look, hair: style })}</button>`).join('');
      const hairColour = Art.HAIRS.map((colour, i) => `<button type="button" class="kb-swatch" style="--kb-swatch:${colour}" data-set="hairColour" data-value="${i}" data-id="hairColour-${i}" ${pressed(look.hairColour === i)} aria-label="Hair colour ${i + 1}"></button>`).join('');
      const toggle = (label, key, value, on) => `<button type="button" class="kb-toggle" data-set="${key}" data-value="${value}" data-id="toggle-${label}" ${pressed(on)}>${label}</button>`;
      const extras =
        toggle('Glasses', 'glasses', !look.glasses, look.glasses) +
        toggle('Bindi', 'bindi', !look.bindi, look.bindi) +
        toggle('Moustache', 'face', look.face === 'moustache' ? 'none' : 'moustache', look.face === 'moustache') +
        toggle('Beard', 'face', look.face === 'beard' ? 'none' : 'beard', look.face === 'beard') +
        (isChild ? '' : toggle('Is a child', 'who', look.who === 'child' ? 'grown-up' : 'child', look.who === 'child'));
      // The last named person cannot be removed: a family book needs somebody in it.
      const others = this.state.family.some((other, index) => index !== this.editing && other.call.trim());
      box.innerHTML =
        (isChild ? '' : `<label class="kb-editor__row"><span class="kb-editor__label">What does your child call them?</span><input class="kb-input" data-call maxlength="16" value="${Art.esc(one.call)}"></label>`) +
        row('Skin', skin) +
        row('Hair', hair) +
        row('Hair colour', hairColour) +
        row('Extras', extras) +
        `<div class="kb-editor__row kb-editor__row--end">${isChild || !others ? '' : '<button type="button" class="kb-link" data-remove data-id="remove">Remove from the book</button>'}<button type="button" class="kb-button kb-button--small" data-done data-id="done">Done</button></div>`;
    }

    familyClick(event) {
      const on = (selector) => event.target.closest(selector);
      let focus = on('[data-id]') ? on('[data-id]').dataset.id : null;
      if (on('[data-person]')) {
        const id = on('[data-person]').dataset.person;
        const target = id === 'child' ? 'child' : this.state.family[Number(id)];
        const open = this.current() !== target;
        if (target !== 'child') this.state.familyChecked = true;
        this.editing = null;
        this.tidyFamily();
        const index = target === 'child' ? 'child' : this.state.family.indexOf(target);
        this.editing = open && index !== -1 ? index : null;
        this.wanted = open ? target : null;
        focus = `person-${index}`;
      } else if (on('[data-add]')) {
        const call = on('[data-add]').dataset.add;
        const someone = call === 'Someone else';
        const was = this.current();
        this.editing = null;
        this.tidyFamily();
        this.state.familyChecked = true;
        const added = member(someone ? '' : call, this.state.family.length + 1);
        this.state.family.push(added);
        this.editing = someone ? this.state.family.length - 1 : was === 'child' ? 'child' : was && this.state.family.includes(was) ? this.state.family.indexOf(was) : null;
        this.wanted = added;
        focus = `person-${this.state.family.length - 1}`;
      } else if (on('[data-set]')) {
        const { set, value } = on('[data-set]').dataset;
        const look = this.editing === 'child' ? this.state.child : this.state.family[this.editing].look;
        look[set] = value === 'true' ? true : value === 'false' ? false : /^\d+$/.test(value) ? Number(value) : value;
        this.wanted = this.current();
      } else if (on('[data-remove]')) {
        this.state.familyChecked = true;
        const [one] = this.state.family.splice(this.editing, 1);
        this.removed = { one, index: this.editing };
        this.editing = null;
        focus = 'undo';
      } else if (on('[data-undo-go]')) {
        this.state.family.splice(Math.min(this.removed.index, this.state.family.length), 0, this.removed.one);
        focus = `person-${this.state.family.indexOf(this.removed.one)}`;
        this.wanted = this.removed.one;
        this.removed = null;
      } else if (on('[data-done]')) {
        const was = this.current();
        this.editing = null;
        this.tidyFamily();
        focus = `person-${was === 'child' ? 'child' : this.state.family.indexOf(was)}`;
      } else if (on('[data-check-yes]')) {
        this.state.familyChecked = true;
        saveState(this.state);
        this.querySelector('[data-check]').hidden = true;
        this.buy();
        return;
      } else if (on('[data-check-change]')) {
        this.querySelector('[data-check]').hidden = true;
        focus = 'person-0';
      } else {
        return;
      }
      if (!on('[data-remove]') && !on('[data-undo-go]') && !on('[data-set]')) this.removed = on('[data-check-change]') ? this.removed : null;
      this.querySelector('[data-check]').hidden = true;
      this.change({});
      this.drawFamily();
      // The faces and choices were drawn again, so the keyboard goes back to where it was.
      const next = this.querySelector(`[data-id="${focus}"]`) || this.querySelector('[data-id="person-child"]');
      if (on('[data-add]') && on('[data-add]').dataset.add === 'Someone else') this.querySelector('[data-call]').focus();
      else next.focus();
    }

    // ----- ordering -----

    link() {
      const x = context(this.state, this.key);
      const data = { n: x.name, c: lookCode(x.child), f: x.family.map((one) => [one.call.trim(), lookCode(one.look)]), m: x.message };
      return `${window.location.origin}${this.dataset.root}pages/my-book?book=${this.key}&d=${pack(data)}`;
    }

    async buy() {
      const name = tidyName(this.state.name);
      this.say('[data-error]', '');
      if (!name) {
        this.say('[data-name-error]', 'Type your child’s name first, so we can put it in the book.');
        this.nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.nameInput.focus({ preventScroll: true });
        return;
      }
      this.tidyFamily();
      this.drawFamily();
      const x = context(this.state, this.key);
      const calls = x.family.map((one) => one.call.trim());
      if (calls.length && !this.state.familyChecked) {
        track('kb_family_check_shown', { book: this.key });
        const check = this.querySelector('[data-check]');
        this.querySelector('[data-check-text]').textContent = `In this book: ${calls.length < 2 ? calls[0] : `${calls.slice(0, -1).join(', ')} and ${calls.at(-1)}`}. Is that everyone?`;
        check.hidden = false;
        check.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.querySelector('[data-check-yes]').focus({ preventScroll: true });
        return;
      }
      const properties = { "Child's name": name };
      if (calls.length) properties.Family = calls.join(', ');
      if (x.message) properties.Message = x.message;
      // The underscore keeps both links out of the cart and checkout: the book is handed over by email, once it is paid for.
      const link = this.link();
      properties['_Your book'] = link;
      properties['_Print file'] = `${link}&print=1`;

      const buttons = [this.buyButton, this.querySelector('[data-bar-buy]')];
      const label = this.buyButton.textContent;
      buttons.forEach((button) => {
        button.disabled = true;
        button.textContent = 'Adding your book…';
      });
      track('kb_book_bought', { book: this.key, pages: this.total, family: x.family.length, message: !!x.message });
      try {
        // Pressing the button again after coming back from the cart must not add the same book twice.
        const cart = await fetch(`${this.dataset.root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json()).catch(() => ({ items: [] }));
        if (!(cart.items || []).some((item) => item.properties && item.properties['_Your book'] === link)) {
          const response = await fetch(`${this.dataset.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.products[Books.BOOKS[this.key].handle].id, quantity: 1, properties }] }),
          });
          if (!response.ok) throw new Error((await response.json()).description || 'The book could not be added.');
        }
        window.location.href = `${this.dataset.root}cart`;
      } catch (problem) {
        this.say('[data-error]', `${problem.message} Please try again.`);
        buttons.forEach((button) => {
          button.disabled = false;
          button.textContent = label;
        });
        this.buyButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  class BookReader extends HTMLElement {
    connectedCallback() {
      ensureDefs();
      const params = new URLSearchParams(window.location.search);
      const made = fromLink(params);
      if (!made) {
        this.querySelector('[data-empty]').hidden = false;
        return;
      }
      const { key, x } = made;
      const title = Books.title(key, x.name);
      // &print=1 on the link gives the file for a printer: bigger pictures, 3 mm of bleed on every side,
      // and endpapers and a back cover to bring the pages to a multiple of four.
      const forPrinter = params.get('print') === '1';
      const pages = forPrinter ? Books.printPages(key, x) : Books.pages(key, x);
      this.querySelector('[data-title]').textContent = title;
      this.querySelector('[data-pages]').innerHTML = pages
        .map((page, index) => {
          const html = Books.pageHtml(page, page.plain ? 1 : index + 1);
          return forPrinter ? `<div class="kb-sheet" style="--kb-bg:${page.bg}">${html}</div>` : html;
        })
        .join('');
      if (forPrinter) this.querySelector('[data-note]').textContent = `Printer’s file: ${pages.length} pages, each a 21.6 cm square. That is the 21 cm page plus 3 mm of bleed on every side. Pictures are 300 to the inch. Save as PDF from Chrome.`;
      this.querySelector('[data-top]').hidden = false;
      document.title = forPrinter ? `${title} (print file)` : title;
      if (!forPrinter) track('kb_book_opened', { book: key });

      // The pictures are flattened in the background as soon as the book opens; printing waits for them.
      // If that fails the book still prints from the drawings, as a bigger file.
      const flat = this.flattenAll(pages, forPrinter ? FLAT_PRINT : FLAT).catch(() => {});
      const button = this.querySelector('[data-print]');
      button.addEventListener('click', async () => {
        const label = button.textContent;
        button.disabled = true;
        button.textContent = 'Getting your book ready…';
        await flat;
        button.textContent = label;
        button.disabled = false;
        if (!forPrinter) track('kb_book_printed', { book: key });
        window.print();
      });
    }

    async flattenAll(pages, size) {
      const font = await pictureFont(lettersIn(pages));
      const arts = this.querySelectorAll('.kb-page__art');
      for (let i = 0; i < pages.length; i += 1) {
        const image = document.createElement('img');
        image.className = 'kb-page__flat';
        image.alt = '';
        image.src = URL.createObjectURL(await flatten(pages[i].art, pages[i].bg, font, size));
        await image.decode();
        arts[i].append(image);
        await new Promise((resolve) => setTimeout(resolve)); // let the page breathe between pictures
      }
      this.classList.add('kb-reader--flat');
    }
  }

  customElements.define('kb-cover', KbCover);
  customElements.define('book-studio', BookStudio);
  customElements.define('book-reader', BookReader);
})();
